import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { ArrowUp, ArrowDown, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  listViewSummarySchema,
  type ListViewState,
  type ListViewIssue,
  type ListViewList,
} from '@erp/shared';
import { apiFetch, apiSend } from '@/lib/api-client';
import { InlineHelp } from '@/features/help';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import type { ListViewController } from './use-list-view';

export function ListViewToolbar({
  controller: c,
  columnLabel,
}: {
  controller: ListViewController;
  columnLabel: (id: string) => string;
}): JSX.Element {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'new' | 'save' | 'rename' | null>(null);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [includeSearch, setIncludeSearch] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [confirm, setConfirm] = useState<'delete' | 'discard' | null>(null);
  const [pendingOpen, setPendingOpen] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const [draft, setDraft] = useState<{
    id: string | null;
    version: number | undefined;
    state: ListViewState;
    list: ListViewList;
  }>({ id: null, version: undefined, state: c.state, list: c.list });
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
    version: number;
  } | null>(null);
  const mutation = useMutation({
    mutationFn: async () => {
      const state: ListViewState = { ...draft.state };
      if (!includeSearch && mode !== 'rename') delete state.search;
      const result = await apiFetch(
        mode === 'new' ? '/api/v1/list-views' : `/api/v1/list-views/${draft.id ?? ''}`,
        listViewSummarySchema,
        {
          method: mode === 'new' ? 'POST' : 'PUT',
          body: {
            list: draft.list,
            name,
            shared,
            state,
            ...(mode === 'new' ? {} : { version: draft.version }),
          },
        },
      );
      await c.refresh();
      c.open(result.id);
      setMode(null);
      toast.success(t('listViews.saved'));
    },
  });
  const action = useMutation({
    mutationFn: async (kind: 'delete' | 'default') => {
      if (kind === 'delete') {
        if (!deleteTarget) return;
        await apiSend(`/api/v1/list-views/${deleteTarget.id}`, {
          method: 'DELETE',
          body: { version: deleteTarget.version },
        });
        c.open(null);
      } else
        await apiSend('/api/v1/list-views/default', {
          method: 'PUT',
          body: {
            list: c.list,
            viewId: c.views.data?.defaultId === c.view?.id ? null : (c.view?.id ?? null),
          },
        });
      await c.refresh();
      toast.success(t('listViews.saved'));
    },
  });
  function edit(next: 'new' | 'save' | 'rename'): void {
    mutation.reset();
    setDraft({
      id: c.view?.id ?? null,
      version: next === 'rename' ? c.view?.version : c.writeVersion,
      state: next === 'rename' ? (c.savedState ?? c.state) : c.state,
      list: c.list,
    });
    setName(next === 'new' ? '' : (c.view?.name ?? ''));
    setShared(next !== 'new' && (c.view?.shared ?? false));
    setIncludeSearch(next !== 'new' && c.savedState?.search !== undefined);
    setMode(next);
  }
  function open(id: string | null): void {
    if (c.dirty) {
      setPendingOpen(id);
      setConfirm('discard');
    } else c.open(id);
  }
  function removeIssue(issue: ListViewIssue): void {
    const state = {
      ...c.state,
      filters: { ...c.state.filters },
      fieldRefs: { ...c.state.fieldRefs },
    };
    if (issue.path.startsWith('filters.customFieldFilters.')) {
      const key = issue.path.slice('filters.customFieldFilters.'.length);
      try {
        const values: Record<string, unknown> = JSON.parse(
          state.filters['customFieldFilters'] ?? '{}',
        );
        delete values[key];
        state.filters['customFieldFilters'] = JSON.stringify(values);
        delete state.fieldRefs[key];
      } catch {
        delete state.filters['customFieldFilters'];
      }
    } else if (issue.path.startsWith('filters.priceGroupIds.')) {
      const id = issue.path.slice('filters.priceGroupIds.'.length);
      const values = (state.filters['priceGroupIds'] ?? '')
        .split(',')
        .filter((value) => value !== id);
      if (values.length) state.filters['priceGroupIds'] = values.join(',');
      else delete state.filters['priceGroupIds'];
    } else if (issue.path.startsWith('filters.'))
      delete state.filters[issue.path.split('.')[1] ?? ''];
    else if (issue.path.startsWith('columns.'))
      state.columns = state.columns.filter((col) => col.id !== issue.path.slice(8));
    else if (issue.path === 'period') delete state.period;
    else {
      c.open(null);
      return;
    }
    c.commit(state);
  }
  return (
    <section className="space-y-2" aria-label={t('listViews.title')}>
      <div className="flex flex-wrap items-center gap-2">
        <InlineHelp articleId="getting-started.list-views" label={t('listViews.title')} />
        {c.views.isPending ? (
          <Skeleton className="h-10 w-48" />
        ) : (
          <select
            className="h-11 max-w-full rounded-md border bg-background px-3 text-sm md:h-9"
            aria-label={t('listViews.title')}
            value={c.view?.id ?? ''}
            onChange={(e) => open(e.target.value || null)}
          >
            <option value="">{t('listViews.standard')}</option>
            {c.views.data?.data.map((view) => (
              <option key={view.id} value={view.id}>
                {view.name}
                {view.shared ? ` · ${t('listViews.shared')}` : ''}
                {c.views.data?.defaultId === view.id ? ' ★' : ''}
              </option>
            ))}
          </select>
        )}
        {c.dirty && (
          <span className="text-xs text-muted-foreground" role="status">
            {t('listViews.dirty')}
          </span>
        )}
        <Button variant="outline" size="sm" disabled={!c.ready} onClick={() => edit('new')}>
          {t('listViews.saveAs')}
        </Button>
        {c.view?.canEdit && (
          <>
            <Button variant="outline" size="sm" disabled={!c.ready} onClick={() => edit('save')}>
              {t('common.save')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={!c.ready || c.dirty}
              title={c.dirty ? t('listViews.dirty') : undefined}
              onClick={() => edit('rename')}
            >
              {t('listViews.rename')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={action.isPending}
              onClick={() => {
                if (!c.view) return;
                action.reset();
                setDeleteTarget({ id: c.view.id, name: c.view.name, version: c.view.version });
                setConfirm('delete');
              }}
            >
              {t('common.delete')}
            </Button>
          </>
        )}
        {(c.view || c.views.data?.defaultId) && (
          <Button
            variant="ghost"
            size="sm"
            disabled={action.isPending}
            onClick={() => action.mutate('default')}
          >
            {t(
              !c.view || c.views.data?.defaultId === c.view.id
                ? 'listViews.clearDefault'
                : 'listViews.makeDefault',
            )}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => open(null)}>
          {t('listViews.reset')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setColumnsOpen(!columnsOpen)}
          aria-expanded={columnsOpen}
        >
          <Settings2 className="h-4 w-4" />
          {t('listViews.columns')}
        </Button>
      </div>
      {(c.error || action.isError) && (
        <div role="alert" className="rounded-md border border-destructive p-3 text-sm">
          {t(c.error ?? action.error?.message ?? 'listViews.invalid')}{' '}
          <Button variant="outline" onClick={c.retry}>
            {t('common.retry')}
          </Button>
        </div>
      )}
      {c.issues.length > 0 && (
        <div role="alert" className="space-y-2 rounded-md border border-destructive p-3 text-sm">
          <p>{t('listViews.blocked')}</p>
          {c.issues.map((issue, i) => (
            <div key={`${issue.path}-${i}`} className="flex flex-wrap items-center gap-2">
              <span>
                {t(`listViews.filterNames.${issue.path.split('.')[1] ?? issue.path}`, {
                  defaultValue: columnLabel(issue.reference),
                })}{' '}
                ({columnLabel(issue.reference)}): {t(`listViews.reasons.${issue.reason}`)}
              </span>
              <Button variant="outline" size="sm" onClick={() => removeIssue(issue)}>
                {t('listViews.removeSetting')}
              </Button>
            </div>
          ))}
        </div>
      )}
      {columnsOpen && (
        <div className="flex flex-wrap gap-3 rounded-md border p-3">
          {c.state.columns.map((column, index) => (
            <div key={column.id} className="flex items-center gap-1">
              <Checkbox
                id={`column-${column.id}`}
                checked={column.visible}
                onCheckedChange={(checked) =>
                  c.commit({
                    ...c.state,
                    columns: c.state.columns.map((col) =>
                      col.id === column.id ? { ...col, visible: checked === true } : col,
                    ),
                  })
                }
              />
              <Label htmlFor={`column-${column.id}`}>{columnLabel(column.id)}</Label>
              {[-1, 1].map((direction) => (
                <Button
                  key={direction}
                  size="icon"
                  variant="ghost"
                  aria-label={t(direction < 0 ? 'listViews.moveUp' : 'listViews.moveDown', {
                    name: columnLabel(column.id),
                  })}
                  disabled={index + direction < 0 || index + direction >= c.state.columns.length}
                  onClick={() => {
                    const columns = [...c.state.columns];
                    const other = columns[index + direction];
                    if (!other) return;
                    columns[index + direction] = column;
                    columns[index] = other;
                    c.commit({ ...c.state, columns });
                  }}
                >
                  {direction < 0 ? (
                    <ArrowUp className="h-3 w-3" />
                  ) : (
                    <ArrowDown className="h-3 w-3" />
                  )}
                </Button>
              ))}
            </div>
          ))}
        </div>
      )}
      <Dialog
        open={mode !== null}
        onOpenChange={(open) => {
          if (!open && !mutation.isPending) setClosing(true);
        }}
      >
        <DialogContent
          closeLabel={t('common.close')}
          hideClose={mutation.isPending}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && ['s', 'Enter'].includes(event.key)) {
              event.preventDefault();
              if (name.trim() && !mutation.isPending && !closing && confirm === null)
                mutation.mutate();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {t(mode === 'rename' ? 'listViews.rename' : 'listViews.saveAs')}
            </DialogTitle>
            <DialogDescription>{t('listViews.description')}</DialogDescription>
          </DialogHeader>
          <Label htmlFor="view-name">{t('listViews.name')}</Label>
          <Input
            id="view-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={mutation.isPending}
            maxLength={100}
            autoFocus
          />
          <div className="flex items-center gap-2">
            <Checkbox
              id="view-shared"
              checked={shared}
              disabled={mutation.isPending || mode !== 'new' || !c.views.data?.canShare}
              onCheckedChange={(v) => setShared(v === true)}
            />
            <Label htmlFor="view-shared">{t('listViews.shared')}</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="view-search"
              disabled={mutation.isPending || mode === 'rename'}
              checked={includeSearch}
              onCheckedChange={(v) => setIncludeSearch(v === true)}
            />
            <Label htmlFor="view-search">{t('listViews.includeSearch')}</Label>
          </div>
          {mutation.isError && (
            <div role="alert" className="space-y-2 text-sm text-destructive">
              <p>{t(mutation.error.message)}</p>
              {draft.id && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setPendingOpen(draft.id);
                    setConfirm('discard');
                  }}
                >
                  {t('listViews.reload')}
                </Button>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => setClosing(true)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              disabled={name.trim() === '' || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={confirm !== null || closing}
        onOpenChange={(open) => {
          if (!open) {
            setConfirm(null);
            setClosing(false);
          }
        }}
      >
        <AlertDialogContent>
          <div className="space-y-2">
            <AlertDialogTitle>
              {t(confirm === 'delete' ? 'listViews.deleteTitle' : 'listViews.discardTitle', {
                name: confirm === 'delete' ? (deleteTarget?.name ?? '') : name,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                confirm === 'delete'
                  ? 'listViews.deleteDescription'
                  : 'listViews.discardDescription',
              )}
            </AlertDialogDescription>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (closing) setMode(null);
                else if (confirm === 'delete') action.mutate('delete');
                else {
                  setMode(null);
                  c.open(pendingOpen);
                }
                setConfirm(null);
                setClosing(false);
              }}
            >
              {t(confirm === 'delete' ? 'common.delete' : 'listViews.discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
